import React, { lazy, Suspense, useState, useEffect, Fragment } from "react";
import { withRouter } from "react-router-dom";
import { Layout, Spin, Row, Button } from "antd";
import { useDispatch } from "react-redux";
import { appSelector } from "../helpers/appSelector";
import { AppDispatch } from "../helpers/appDispatch";

import {
  getTransactionsRequest,
  exportTranxRequest,
  downloadReceiptRequest,
} from "../store/vas-transactions";
import { getMerchantsRequest, getVASServiceRequest } from "../store/reports";
import { isEmpty } from "../helpers/isEmpty";
import {
  TransactionHistory,
  MerchantData,
  VASService,
} from "../interfaces";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Accounts from "./_accounts";
import { getAccountsBalanceRequest } from "../store/home";

const { Content } = Layout;

const TransactionFilters = lazy(
  () => import("../components/vas-transactions/TransactionFilters")
);
const TransactionTable = lazy(
  () => import("../components/vas-transactions/TransactionTable")
);
const TransactionDetail = lazy(
  () => import("../components/vas-transactions/TransactionDetail")
);
const CurrencyFilter = lazy(
  () => import("../components/vas-transactions/CurrencyFilter")
);
const EmptyBox = lazy(() => import("../components/vas-transactions/EmptyBox"));

const VasTransactions = () => {
  const dispatch: AppDispatch = useDispatch();

  //Modal

  // const [imageUrl, setImageUrl] = useState('')

  const transaction = appSelector((state) => state.vasTransaction);
  const reports = appSelector((state) => state.reports);
  const { user } = appSelector((state) => state.auth);
  const home = appSelector((state) => state.home)
  const { t } = useTranslation();
  const [channelSearch, setChannelSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [statusSearch, setStatusSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [exportType, setExportType] = useState("");
  const [trx, setTrx] = useState<TransactionHistory | null>(null);
  const [switchView, setSwitchView] = useState(false);
  const [currency, setCurrency] = useState("USD");
  const [merchants, setMerchants] = useState<MerchantData[]>(reports.merchants);
  const [merchant, setMerchant] = useState<MerchantData | null>(null);
  const [pageSize, setPageSize] = useState(10);
  const [skip, setSkip] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [processType, setProcessType] = useState("");
  const [vasservices, setVasServices] = useState<VASService[]>(
    reports.vasservices
  );
  const { accounts, loadingAccounts } = home
  const [currentPage, setCurrentPage] = useState(1);
  const trans =
    transaction.transactions && !isEmpty(transaction.transactions.data)
      ? transaction.transactions.data || []
      : [];
  const trxReport = transaction.transactions || null;
  const loading = transaction.loading;
  const isExporting = transaction.isExporting;
  const isDownlaoding = transaction.isRequestingDownload;

  const params = {
    currency: currency,
    pageSize: pageSize,
    skip: skip,
    periodFrom: fromDate,
    periodTo: toDate,
    merchant: merchant ? merchant.name : "",
    status: statusSearch,
    channel: channelSearch,
    searchValue: searchValue,
    exportType: exportType,
    fixedPeriod: "overall",
    processTypeId: processType,
  };

  useEffect(() => {
    // fetch transaction history
    // dispatch(getTransactionsRequest(params))
    const { merchants } = reports;
    if (isEmpty(merchants)) {
      dispatch(getMerchantsRequest());
    }
    dispatch(getVASServiceRequest());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      dispatch(getAccountsBalanceRequest())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    const { merchants, vasservices } = reports;
    setMerchants(merchants);
    setVasServices(vasservices);
  }, [reports]);

  const onClickRow = (transactionID: number) => {
    if (!isEmpty(trans)) {
      const trx = trans.find(
        (t) => t.transactionId === transactionID
      );
      if (trx !== undefined) {
        setTrx(trx);
        setSwitchView(true);
      }
    }
  };

  const onCloseScreen = () => {
    setSwitchView(false);
    setTrx(null);
  };

  const reloadTransaction = () => {
    dispatch(getTransactionsRequest(params));
  };

  const onDownloadReceiptClick = (transactionId: number): void => {
    dispatch(downloadReceiptRequest(transactionId));
  };

  const onSelectCurrency = (value: string) => {
    setCurrency(value);
    setSkip(0);
    setCurrentPage(1);
    setMerchant(null);
    dispatch(getAccountsBalanceRequest());
    
    const searchParams = {
      currency: value,
      pageSize,
      skip: 0,
      periodFrom: fromDate,
      periodTo: toDate,
      status: "",
      channel: "",
      searchValue: "",
      merchant: "",
      exportType: "",
      fixedPeriod: "overall",
      processTypeId: processType,
    };
    
    dispatch(getTransactionsRequest(searchParams));
  };

  const getSearchParams = () => ({
    currency,
    pageSize,
    skip,
    periodFrom: fromDate,
    periodTo: toDate,
    merchant: merchant?.name || "",
    status: statusSearch,
    channel: channelSearch,
    searchValue: searchValue,
    exportType: exportType,
    fixedPeriod: "overall",
    processTypeId: processType,
  });

  const onLoadMore = (page: number, size: number) => {
    const newSkip = page - 1;
    setPageSize(size);
    setSkip(newSkip);
    setCurrentPage(page);
    
    const searchParams = getSearchParams();
    searchParams.skip = newSkip;
    searchParams.pageSize = size;
    
    dispatch(getTransactionsRequest(searchParams));
  };

  const onExportClick = (type: string) => {
    setExportType(type);
    params.exportType = type;
    dispatch(exportTranxRequest(params));
  };

  const onSearch = (values: any) => {
    setIsSearching(true);
    setSwitchView(false);
    setTrx(null);
    setSkip(0);
    setCurrentPage(1);
    
    const {
      status,
      channel,
      periodFrom,
      periodTo,
      query,
      merchant: merchantId,
      processTypeId,
    } = values;
    
    let m: MerchantData | undefined = undefined;
    let pFrom: string = "",
      pTo: string = "";

    setChannelSearch(channel !== undefined ? channel : "");
    setStatusSearch(status !== undefined ? status : "");
    setSearchValue(query !== undefined ? query : "");
    
    if (periodFrom !== undefined && periodTo !== undefined) {
      pFrom = moment(periodFrom).format("MM/DD/YYYY");
      pTo = moment(periodTo).format("MM/DD/YYYY");
      setFromDate(pFrom);
      setToDate(pTo);
    }
    
    if (merchantId !== undefined && merchantId !== null && merchantId !== "") {
      const selectedMerchantId = Number(merchantId);
      const merchantList = !isEmpty(merchants) ? merchants : reports.merchants;
      m = merchantList.find((merchant) => merchant.merchantId === selectedMerchantId);
      setMerchant(m !== undefined ? m : null);
    } else {
      setMerchant(null);
    }
    dispatch(getAccountsBalanceRequest());
    
    setProcessType(processTypeId);

    const searchParams = {
      currency,
      pageSize,
      skip: 0,
      periodFrom: pFrom,
      periodTo: pTo,
      merchant: m?.name || "",
      status: status || "",
      channel: channel || "",
      searchValue: query || "",
      exportType,
      fixedPeriod: "overall",
      processTypeId,
    };

    dispatch(getTransactionsRequest(searchParams));
  };

  const onReset = (form: any) => {
    form.resetFields();
    setSwitchView(false);
    setTrx(null);
    setSkip(0);
    setCurrentPage(1);
    setMerchant(null);
    dispatch(getAccountsBalanceRequest());
    setFromDate("");
    setToDate("");
    setStatusSearch("");
    setChannelSearch("");
    setSearchValue("");
    setProcessType("");
    setIsSearching(false);
    
    const resetParams = {
      currency,
      pageSize: 10,
      skip: 0,
      periodFrom: "",
      periodTo: "",
      merchant: "",
      status: "",
      channel: "",
      searchValue: "",
      exportType: "",
      fixedPeriod: "overall",
      processTypeId: "",
    };

    dispatch(getTransactionsRequest(resetParams));
  };

  return (
    <div className="padding-box">
      <Content className="site-layout-background site-box">
        <Suspense
          fallback={
            <Row
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <div style={{ marginTop: "200px" }}>
                <Spin />
              </div>
            </Row>
          }
        >
          <Fragment>
            <Accounts
              accounts={accounts}
              loading={loadingAccounts}
            />
            <TransactionFilters
              merchants={merchants}
              onSearch={onSearch}
              onReset={onReset}
              vasservices={vasservices}
              translate={t}
            />
            {isSearching ? (
              <Fragment>
                {!switchView ? (
                  <Fragment>
                    {/* <TransactionSummaryCards
                      trxReports={trxReport}
                      currency={currency}
                      loading={loading}
                      translate={t}
                    /> */}

                    <CurrencyFilter
                      onSelectCurrency={onSelectCurrency}
                      translate={t}
                    />
                    <div className="margin-top">
                      <Row style={{ position: "relative" }}>
                        <h4 className="transaction-chart-text">
                          {t("general.transactionsChart")}
                        </h4>
                        <div className="utility-buttons">
                          {!isEmpty(trans) ? (
                            <>
                              <Button
                                type="primary"
                                className="export-buttons"
                                onClick={() => onExportClick("EXCEL")}
                                loading={isExporting && exportType === "EXCEL"}
                              >
                                {t("general.export-excel")}
                              </Button>
                              {/* <Button
                          type="primary"
                          className="export-buttons"
                          onClick={() => onExportClick('PDF')}
                          loading={isExporting && exportType === 'PDF'}
                        >
                          {t('general.export-pdf')}
                        </Button> */}
                            </>
                          ) : null}
                          <Button
                            type="primary"
                            className="export-buttons reload"
                            onClick={() => reloadTransaction()}
                          >
                            {t("general.refresh")}
                          </Button>
                        </div>
                      </Row>

                      <TransactionTable
                        transactionHistory={trans}
                        onClickRow={onClickRow}
                        currency={currency}
                        loading={loading}
                        onLoadMore={onLoadMore}
                        total={trxReport?.total?.value || trans.length}
                        translate={t}
                        currentPage={currentPage}
                      />
                    </div>
                  </Fragment>
                ) : (
                  <TransactionDetail
                    onCloseScreen={onCloseScreen}
                    transaction={trx!}
                    isDownloading={isDownlaoding}
                    onDownloadReceiptClick={onDownloadReceiptClick}
                    translate={t}
                  />
                )}
              </Fragment>
            ) : (
              <EmptyBox header={t("general.filterToGetData")} description="" />
            )}
          </Fragment>

          {/* <PaymentTypeModal
            choosePaymentPage={choosePaymentPage}
            onTogglePaymentTypeModal={onTogglePaymentTypeModal}
            showPaymentTypeModal={showPaymentTypeModal}
            translate={t}
          /> */}
          {/* <FormModal
            onToggleFormModal={onToggleFormModal}
            showFormModal={showFormModal}
            values={initialValues}
            onSubmit={onSubmit}
            isSubmit={isSubmitting}
            translate={t}
          /> */}
        </Suspense>
      </Content>
    </div>
  );
};

export default withRouter(VasTransactions);
